import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

const formatCashbackK = (total: number) =>
  total >= 1000 ? `$${(total / 1000).toFixed(1)}k` : `$${total.toFixed(0)}`;

const Landing: React.FC = () => {
  const [totalCashbackIssued, setTotalCashbackIssued] = useState<number | null>(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
    fetch(`${apiUrl}/stats`)
      .then(res => res.json())
      .then(data => setTotalCashbackIssued(data.totalCashbackIssued ?? 0))
      .catch(() => setTotalCashbackIssued(0));
  }, []);

  const heroSlides = useMemo(
    () => [
      {
        title: 'A premium shopping experience for Club Kazi members',
        image:
          'https://images.unsplash.com/photo-1481931098730-318b6f776db0?w=1200&h=900&fit=crop',
      },
      {
        title: 'Get the best value for your money',
        image:
          'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=1200&h=900&fit=crop',
      },
      {
        title: 'Extreme savings on your favourite groceries',
        image:
          'https://images.unsplash.com/photo-1511690743698-d9d85f2fbf38?w=1200&h=900&fit=crop',
      },
    ],
    []
  );
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  return (
    <div className="landing">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-eyebrow">Members-Only Grocery Club</div>
          <h1 className="hero-title">
            {heroSlides[activeSlide].title.split('Club Kazi').map((chunk, index, parts) => {
              const words = chunk.split(/(Extreme savings|best value)/);
              return (
                <React.Fragment key={`${chunk}-${index}`}>
                  {words.map((part, i) =>
                    part === 'Extreme savings' || part === 'best value' ? (
                      <span key={`${part}-${i}`} className="hero-emphasis">
                        {part}
                      </span>
                    ) : (
                      <span key={`${part}-${i}`}>{part}</span>
                    )
                  )}
                  {index < parts.length - 1 && (
                    <span className="brand-highlight">Club Kazi</span>
                  )}
                </React.Fragment>
              );
            })}
          </h1>
          <p className="hero-subtitle">
            Curated essentials, transparent pricing, and a wallet-first checkout. Enjoy exclusive savings without
            the hassle of hidden charges or surprise fees.
          </p>
          <div className="hero-buttons">
            <Link to="/register" className="btn btn-primary btn-large">
              Become a Member
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="hero-metrics">
        <div className="container metrics-grid">
          <div className="metric">
            <div className="metric-value">0%</div>
            <div className="metric-label">Membership Fees</div>
          </div>
          <div className="metric">
            <div className="metric-value">
              {totalCashbackIssued !== null ? formatCashbackK(totalCashbackIssued) : '—'}
            </div>
            <div className="metric-label">Total Cashback Issued</div>
          </div>
          <div className="metric">
            <div className="metric-value">20%</div>
            <div className="metric-label">Member Savings</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Why Club Kazi Feels Exclusive</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">💳</div>
              <h3>Wallet-First Experience</h3>
              <p>Load your wallet once and checkout instantly—no card details required at every purchase.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>Zero Fees, Best Prices</h3>
              <p>Transparent pricing with visible savings on every product—no hidden fees, ever.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Elevated Checkout</h3>
              <p>Seamless, secure, and fast—keep your focus on what matters: premium essentials.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Join an Exclusive Shopping Club</h2>
          <p>Premium groceries, zero fees, and effortless wallet checkout—built for members.</p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-primary btn-large">
              Become a Member
            </Link>
            <Link to="/plans" className="btn btn-outline btn-large">
              View Membership Plans
            </Link>
            <Link to="/about" className="btn btn-outline btn-large">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="container footer-content">
          <div>
            <h4>Contact</h4>
            <p>hello@kazifoods.ca</p>
            <p>
              <a href="tel:+14379968627" className="footer-contact-link">
                +1 (437) 996-8627
              </a>
            </p>
            <p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=1095%20Lakeshore%20Road%20E%2C%20Mississauga%2C%20ON%20L5E%201E8"
                target="_blank"
                rel="noreferrer"
                className="footer-contact-link"
              >
                1095 Lakeshore Road E, Mississauga, ON L5E 1E8
              </a>
            </p>
          </div>
          <div>
            <h4>Legal</h4>
            <div className="footer-links">
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/legal">Terms & Legal</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
